import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Panel from "../../components/brand/Panel";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

const sectionLabel = (section) => {
  if (section.section_type === "estate") return "Estate Charge";
  return "Block Charge";
};

const sanitizeMoney = (value) => {
  const cleaned = String(value).replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

const budgetStatusLabel = (status) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " ") : "";

const budgetStatusColor = (status) =>
  status === "published" ? "success" : status === "approved" ? "info" : "default";

const lineAmount = (line) => {
  const amount = Number(line.amount);
  return Number.isFinite(amount) ? amount : 0;
};

const groupedLinesByCategory = (lines) => {
  const groups = new Map();
  lines.forEach((line) => {
    const category = line.category || "Uncategorised";
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(line);
  });
  return Array.from(groups, ([category, categoryLines]) => ({
    category,
    lines: categoryLines.sort(
      (a, b) =>
        (a.account_code || "").localeCompare(b.account_code || "") ||
        a.name.localeCompare(b.name)
    ),
  })).sort((a, b) => a.category.localeCompare(b.category));
};

export default function BudgetEditor({ budget, onSave, onStatus, onOwnerVisibility }) {
  const [draft, setDraft] = useState(budget);
  const [saving, setSaving] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);

  React.useEffect(() => {
    setDraft(budget);
  }, [budget]);

  const hasPreviousYear = (draft.sections || []).some((section) =>
    (section.lines || []).some((line) => line.previous_amount != null)
  );

  const updateLineAmount = (sectionId, lineId, amount) => {
    const cleaned = sanitizeMoney(amount);
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id !== sectionId
          ? section
          : {
              ...section,
              lines: section.lines.map((line) =>
                line.id === lineId ? { ...line, amount: cleaned } : line
              ),
            }
      ),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        row_version: draft.row_version,
        name: draft.name,
        sections: draft.sections.map((section, sIdx) => ({
          id: section.id,
          name: section.name,
          section_type: section.section_type,
          sort_order: sIdx,
          lines: section.lines.map((line, lIdx) => ({
            id: line.id,
            name: line.name,
            line_item_id: line.line_item_id,
            amount: line.amount === "" || line.amount == null ? 0 : Number(line.amount),
            previous_amount: line.previous_amount,
            sort_order: lIdx,
          })),
        })),
      });
    } finally {
      setSaving(false);
    }
  };

  const readOnly = draft.status === "published";
  const canShowToOwners = draft.status === "approved" || draft.status === "published";
  const sectionTotal = (section) =>
    (section.lines || []).reduce((sum, line) => sum + lineAmount(line), 0);
  const budgetTotal = (draft.sections || []).reduce((sum, section) => sum + sectionTotal(section), 0);
  const statusHint =
    draft.status === "published"
      ? draft.visible_to_owners
        ? "Published and locked. Owners can see this budget on the client portal."
        : "Published and locked. Amounts cannot be changed. Owners cannot see it until you show it to them."
      : draft.status === "approved"
        ? draft.visible_to_owners
          ? "Approved. Invoices can use these amounts. Publish to lock them. Owners can see this budget on the client portal."
          : "Approved. Invoices can use these amounts. Publish to lock them. Owners cannot see it until you show it to them."
        : "Draft. Enter the amounts, then approve it when it is ready.";

  const handleOwnerVisibility = async () => {
    if (!onOwnerVisibility) return;
    setVisibilitySaving(true);
    try {
      await onOwnerVisibility(!draft.visible_to_owners);
    } finally {
      setVisibilitySaving(false);
    }
  };

  if (!(draft.sections || []).length) {
    return (
      <Panel title={draft.name} noBodyPadding={false}>
        <Alert severity="warning">
          This budget has no charges yet.
          {draft.status === "draft"
            ? " Use Edit charge list, save the charges, then create the budget again."
            : ""}
        </Alert>
      </Panel>
    );
  }

  return (
    <Panel
      title={draft.name}
      noBodyPadding={false}
      right={
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Chip
            label={budgetStatusLabel(draft.status)}
            size="small"
            color={budgetStatusColor(draft.status)}
          />
          {draft.status === "draft" && (
            <Button size="small" variant="outlined" startIcon={<CheckIcon />} onClick={() => onStatus("approve")}>
              Approve
            </Button>
          )}
          {draft.status === "approved" && (
            <Button size="small" variant="outlined" startIcon={<DoneAllIcon />} onClick={() => onStatus("publish")}>
              Publish
            </Button>
          )}
          {canShowToOwners && (
            <Button
              size="small"
              variant="outlined"
              disabled={visibilitySaving}
              startIcon={draft.visible_to_owners ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={handleOwnerVisibility}
            >
              {draft.visible_to_owners ? "Hide from owners" : "Show to owners"}
            </Button>
          )}
          {!readOnly && (
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </Box>
      }
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          {!readOnly && (
            <TextField
              fullWidth
              size="small"
              label="Budget name"
              value={draft.name || ""}
              onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 1 }}
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {statusHint}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
            Total
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {money(budgetTotal)}
          </Typography>
        </Box>
      </Box>

      {(draft.sections || []).map((section) => (
        <Box
          key={section.id}
          sx={(theme) => ({
            p: 2,
            mb: 2,
            borderRadius: 1,
            border: `1px solid ${theme.palette.brand?.border || theme.palette.divider}`,
          })}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, gap: 2 }}>
            <Box>
              <Typography fontWeight={600}>{section.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {sectionLabel(section)}
              </Typography>
            </Box>
            <Typography variant="body2">{money(sectionTotal(section))}</Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Budget line</TableCell>
                  {hasPreviousYear && <TableCell align="right">Previous year</TableCell>}
                  <TableCell align="right">This year</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedLinesByCategory(section.lines || []).map((group) => (
                  <React.Fragment key={group.category}>
                    <TableRow>
                      <TableCell
                        colSpan={hasPreviousYear ? 4 : 3}
                        sx={{ bgcolor: "action.hover", fontWeight: 700 }}
                      >
                        {group.category}
                      </TableCell>
                    </TableRow>
                    {group.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.account_code || "—"}</TableCell>
                        <TableCell>{line.name}</TableCell>
                        {hasPreviousYear && (
                          <TableCell align="right">{money(line.previous_amount)}</TableCell>
                        )}
                        <TableCell align="right">
                          {readOnly ? (
                            money(line.amount)
                          ) : (
                            <TextField
                              size="small"
                              value={line.amount}
                              onChange={(e) =>
                                updateLineAmount(section.id, line.id, e.target.value)
                              }
                              sx={{ width: 140 }}
                              inputProps={{
                                style: { textAlign: "right" },
                                inputMode: "decimal",
                              }}
                              InputProps={{
                                startAdornment: (
                                  <Box component="span" sx={{ mr: 0.5, color: "text.secondary" }}>
                                    £
                                  </Box>
                                ),
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {section.section_type !== "estate" &&
            (section.unit_allocations || []).length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {(section.unit_allocations || []).length === 1
                  ? "1 unit assigned. Edit it on the Apportionments tab."
                  : `${(section.unit_allocations || []).length} units assigned. Edit them on the Apportionments tab.`}
              </Typography>
            )}
        </Box>
      ))}
      {!readOnly && (
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
          {saving ? "Saving…" : "Save"}
        </Button>
      )}
    </Panel>
  );
}
